import React, { useCallback, useMemo, useState, useEffect, SetStateAction, Dispatch } from 'react';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import Button from 'components/commercetools-ui/atoms/button';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import Info from 'components/commercetools-ui/atoms/info';
import Input from 'components/commercetools-ui/atoms/input';
import Loading from 'components/commercetools-ui/atoms/loading';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useProcessing from 'helpers/hooks/useProcessing';
import useValidate from 'helpers/hooks/useValidate';
import { useAccount, useCart } from 'frontastic';
import { CartDetails } from 'frontastic/hooks/useCart/types';
import AccountAddresses from './components/account-addresses';
import AddressForm from './components/address-form';
import { Fields, FieldsOptions } from './components/address-form/types';
import useMappers from './hooks/useMappers';
import { Address } from './types';
import CollectInStore from '../../../collect-in-store';
import { validateCartForRestrictedItems } from '../payment/helper/apply-pay-helper';

export interface Props {
  setIsDeliveryError: Dispatch<SetStateAction<boolean>>;
  goToNextStep: () => void;
  active: number;
  selectedDeliveryOption: string;
  retainData?: boolean;
  resetAddressData?: string;
}

interface AddressInfo {
  addressType?: string;
  line1?: string;
  line2?: string;
  postcode?: string;
  town?: string;
  postalCounty?: string;
  longitude?: number;
  latitude?: number;
}

const initialAddressData = {
  addressId: '',
  title: 'Mr',
  firstName: '',
  lastName: '',
  email: '',
  phone: '', // mobile
  line1: '',
  line2: '',
  postalCode: '',
  city: '',
  county: '',
  country: '',
} as Address;

const Addresses: React.FC<Props> = ({
  goToNextStep,
  active,
  setIsDeliveryError,
  selectedDeliveryOption,
  retainData = false,
  resetAddressData,
}) => {
  const { locale, getCountry } = useLocale();
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const [collectInStoreOverlayOpen, setCollectInStoreOverlayOpen] = useState(false);
  const [deliveryInstruction, setDeliveryInstructionLocalState] = useState<string | null>(null);
  const { validateEmail, validatePhoneNumeric, validatePostalCode } = useValidate();
  const { account, loggedIn, shippingAddresses, billingAddresses, addBillingAddress, addShippingAddress } =
    useAccount();
  const { data, updateCart, setDeliveryInstruction, getAvailableShippingMethods } = useCart();
  const { addressToAccountAddress, accountAddressToAddress } = useMappers();
  const [retainDataCheckDone, setRetainDataCheckDone] = useState(false);

  initialAddressData.country = getCountry();

  const addressesAreEqual = useCallback((address1: Address, address2: Address) => {
    const keys = Object.keys(address1).filter(
      (key) => key !== 'addressId' && key !== 'custom' && key in address2,
    ) as Array<keyof Address>;
    return keys.every((key) => address1[key] === address2[key]);
  }, []);

  const isEmptyAddress = useCallback((address: Address) => {
    const excludedKeys: Array<keyof Address> = ['addressId', 'country', 'title'];

    return !Object.keys(address).some(
      (key) => !excludedKeys.includes(key as keyof Address) && address[key as keyof Address] !== '',
    );
  }, []);

  const [shippingAddress, setShippingAddress] = useState(initialAddressData);
  const [billingAddress, setBillingAddress] = useState(initialAddressData);
  const [sameAddressCheckDone, setSameAddressCheckDone] = useState(false);
  const [sameShippingAddress, setSameShippingAddress] = useState(
    !isEmptyAddress(billingAddress) && !addressesAreEqual(shippingAddress, billingAddress) ? false : true,
  );

  useEffect(() => {
    if (!isEmptyAddress(billingAddress) && !sameAddressCheckDone) {
      setSameAddressCheckDone(true);
      if (addressesAreEqual(shippingAddress, billingAddress)) {
        setSameShippingAddress(true);
      } else {
        setSameShippingAddress(false);
      }
    }
  }, [shippingAddress, billingAddress, addressesAreEqual, active, isEmptyAddress]);

  useEffect(() => {
    setShippingAddress(initialAddressData);
    setBillingAddress(initialAddressData);
    //setShippingAddress(accountAddressToAddress(shippingAddresses[0]));
    if (resetAddressData == 'StoreCollection') {
      setTimeout(() => {
        setCollectInStoreOverlayOpen(true);
      }, 2000);
    } else {
      setSameShippingAddress(true);
    }
  }, [resetAddressData]);

  useEffect(() => {
    const retainAddressDetails = () => {
      setRetainDataCheckDone(true);
      if (data?.shippingAddress?.postalCode) {
        setShippingAddress(accountAddressToAddress(data?.shippingAddress));
        setShippingAddress((shippingAddress) => ({
          ...shippingAddress,
          email: data.email || '',
        }));
      }
      if (data?.billingAddress?.postalCode) {
        setBillingAddress(accountAddressToAddress(data?.billingAddress));
        setBillingAddress((billingAddress) => ({
          ...billingAddress,
          email: data.email || '',
        }));
      }
    };
    if (data && !retainDataCheckDone && retainData) {
      retainAddressDetails();
    }
  }, [data]);

  useEffect(() => {
    const addressSection = document.getElementById('addressSection');
    const addressLoading = document.getElementById('address-loading');

    if (addressSection?.style.display === 'none') {
      if (addressLoading) {
        addressLoading.style.display = 'block';
      }

      setTimeout(() => {
        addressSection.style.display = 'block';

        if (addressLoading) {
          addressLoading.style.display = 'none';
        }
      }, 1000);
    } else {
      setTimeout(() => {
        if (addressLoading) {
          addressLoading.style.display = 'none';
        }
      }, 1000);
    }
  });

  const currentBillingAddress = useMemo(
    () => (sameShippingAddress ? shippingAddress : billingAddress),
    [sameShippingAddress, shippingAddress, billingAddress],
  );

  const baseSchema = {
    phone: yup.string().required(),
    line1: yup.string().required(),
    line2: yup.string().optional(),
    postalCode: yup.string().required(),
    city: yup.string().required(),
  };

  const addressValidationSchema = useMemo(() => {
    return yup.object().shape({
      ...baseSchema,
      title: yup.string().required(),
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      // email: yup.string().email().required(),
      line1: yup.string().required(),
      line2: yup.string().optional(),
      postalCode: yup.string().required(),
      city: yup.string().required(),
    });
  }, [baseSchema]);

  const storeAddressValidationSchema = useMemo(() => {
    return yup.object().shape({
      line1: yup.string().required(),
      line2: yup.string().optional(),
      postalCode: yup.string().required(),
      city: yup.string().required(),
    });
  }, [baseSchema]);

  const isValidStoreAddress = useMemo(() => {
    try {
      storeAddressValidationSchema.validateSync(shippingAddress);
      return true;
    } catch (err) {
      return false;
    }
  }, [storeAddressValidationSchema, shippingAddress]);

  const isValidShippingAddress = useMemo(() => {
    try {
      if (loggedIn && shippingAddress.addressId) {
        return true;
      }
      addressValidationSchema.validateSync(shippingAddress);
      const postCodeValid = validatePostalCode(shippingAddress.postalCode, shippingAddress.country);
      const phoneNumberValid = validatePhoneNumeric(shippingAddress.phone || '');
      const emailValid = validateEmail(shippingAddress.email || '');
      if (!postCodeValid || !phoneNumberValid || (!loggedIn && !emailValid)) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  }, [addressValidationSchema, shippingAddress]);

  const isValidBillingAddress = useMemo(() => {
    try {
      if (loggedIn && currentBillingAddress.addressId) {
        return true;
      }
      addressValidationSchema.validateSync(currentBillingAddress);
      const postCodeValid = validatePostalCode(currentBillingAddress.postalCode, currentBillingAddress.country);
      const phoneNumberValid = validatePhoneNumeric(currentBillingAddress.phone || '');
      if (!postCodeValid || !phoneNumberValid) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  }, [addressValidationSchema, currentBillingAddress]);

  const isValidStoreBillingAddress = useMemo(() => {
    try {
      addressValidationSchema.validateSync(currentBillingAddress);
      const postCodeValid = validatePostalCode(currentBillingAddress.postalCode, currentBillingAddress.country);
      const phoneNumberValid = validatePhoneNumeric(currentBillingAddress.phone || '');
      const emailValid = validateEmail(currentBillingAddress.email || '');
      if (!postCodeValid || !phoneNumberValid || (!loggedIn && !emailValid)) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  }, [addressValidationSchema, currentBillingAddress]);

  const handleShippingAddressChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDeliveryInstruction = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue.length <= 32) {
      setDeliveryInstructionLocalState(inputValue);
    }
  };

  const updateAddress = (address: Address, addressInfo: AddressInfo) => ({
    ...address,
    line1: addressInfo.line1 || '',
    line2: addressInfo.line2 || '',
    city: addressInfo.town || '',
    county: addressInfo.postalCounty || '',
    postalCode: addressInfo.postcode || '',
  });

  const onAFDAddressSelection = (addressInfo: AddressInfo) => {
    if (addressInfo.addressType === 'shipping') {
      setShippingAddress((shippingAddress) => updateAddress(shippingAddress, addressInfo));
    } else if (addressInfo.addressType) {
      setBillingAddress((billingAddress) => updateAddress(billingAddress, addressInfo));
    }
  };

  const onAddressCountrySelection = (countryCode: string, addressType: string) => {
    if (addressType == 'shipping') {
      setShippingAddress({ ...shippingAddress, country: countryCode });
    } else {
      setBillingAddress({ ...billingAddress, country: countryCode });
    }
  };

  const onSelectedTitle = (selectedTitle: string, addressType: string) => {
    if (addressType == 'shipping') {
      setShippingAddress({ ...shippingAddress, title: selectedTitle });
    } else {
      setBillingAddress({ ...billingAddress, title: selectedTitle });
    }
  };

  const handleBillingAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setBillingAddress((prev) => ({ ...prev, [name]: value }));
    },
    [billingAddress, isEmptyAddress],
  );

  const handleCheckboxChange = useCallback(
    (e: { checked: boolean }) => {
      setSameShippingAddress(e.checked);
      if (!loggedIn) {
        if (e.checked) {
          setBillingAddress(shippingAddress);
        } else {
          setBillingAddress(initialAddressData);
        }
      }
    },
    [shippingAddress],
  );

  const { processing, startProcessing, stopProcessing } = useProcessing();

  const submit = useCallback(async () => {
    const isInvalidAddress =
      selectedDeliveryOption === 'StoreCollection' ? !isValidStoreAddress : !isValidShippingAddress;
    if (isInvalidAddress || !isValidBillingAddress || processing) return;

    startProcessing();

    const checkoutData = {
      account: {
        email: loggedIn
          ? account?.email
          : selectedDeliveryOption !== 'StoreCollection'
          ? shippingAddress.email
          : currentBillingAddress.email,
      },
      shipping: addressToAccountAddress(shippingAddress),
      billing: addressToAccountAddress(currentBillingAddress),
    } as CartDetails;

    const res = await updateCart(checkoutData);
    const salutation = loggedIn ? account?.salutation || '' : '';
    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerHelpers(
      currentUrl,
      locale,
      data as Record<string, any>,
      account?.email,
      account?.accountId,
      salutation,
    );
    const dataLayerData = {
      breadcrumb: 'Checkout / Shipping',
      event: 'checkout',
      step: 2,
      option: '',
      store: shippingAddress?.line1 || '',
    };

    const isItemRestricted = validateCartForRestrictedItems(res, addressToAccountAddress(shippingAddress));
    const productShippingMethods = await getAvailableShippingMethods();
    const isDeliveryMethodError = productShippingMethods.length === 0;
    setIsDeliveryError(isDeliveryMethodError || isItemRestricted);

    dataLayer.setCartDataLayer(dataLayerData);
    stopProcessing();

    if (res.cartId) goToNextStep();
    else
      toast.error(
        formatCheckoutMessage({
          id: 'update.addresses.error',
          defaultMessage: "Couldn't update your addresses information, please try again later.",
        }),
        { position: 'bottom-left' },
      );
  }, [
    account,
    isValidShippingAddress,
    isValidBillingAddress,
    isValidStoreBillingAddress,
    shippingAddress,
    currentBillingAddress,
    addressToAccountAddress,
    updateCart,
    goToNextStep,
    formatCheckoutMessage,
    processing,
    startProcessing,
    stopProcessing,
  ]);

  const isStoreCollection = selectedDeliveryOption === 'StoreCollection';
  const isStoreAddressInvalid = !isValidStoreAddress;
  const isShippingAddressInvalid = !isValidShippingAddress;
  const isBillingAddressInvalid = !isValidBillingAddress;
  const isStoreBillingAddressInvalid = !isValidStoreBillingAddress;
  const isButtonDisabled = isStoreCollection
    ? isStoreAddressInvalid || isStoreBillingAddressInvalid
    : isShippingAddressInvalid || isBillingAddressInvalid;

  const handleButtonClick = async () => {
    if (loggedIn) {
      handleAddressUpdates();
    }
    if (selectedDeliveryOption === 'HomeDelivery' && deliveryInstruction) {
      setDeliveryInstruction(deliveryInstruction);
    }
    submit();
  };

  const handleAddressUpdates = () => {
    if (shippingAddresses.length === 0 && isValidShippingAddress) {
      updateShippingAddress();
    }

    if (billingAddresses.length === 0 && isValidBillingAddress) {
      updateBillingAddress();
    }
  };

  const updateShippingAddress = () => {
    const updatedAddress = addressToAccountAddress({
      ...shippingAddress,
      country: shippingAddress.country || shippingAddress.country === '' ? 'GB' : shippingAddress.country,
    });
    addShippingAddress(updatedAddress);
  };

  const updateBillingAddress = () => {
    const address = sameShippingAddress ? shippingAddress : billingAddress;
    const updatedAddress = addressToAccountAddress({
      ...address,
      country: address.country || address.country === '' ? 'GB' : address.country,
    });
    addBillingAddress(updatedAddress);
  };

  const fields = useCallback(
    ({ enableAddress2, onEnableAddress2 }: FieldsOptions) => {
      const fieldList: Fields[] = [
        {
          name: 'firstName',
          label: formatMessage({ id: 'firstName', defaultMessage: 'First Name' }),
          labelDesc: '',
          required: true,
          type: 'string',
          maxLength: 21,
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'lastName',
          label: formatMessage({ id: 'lastName', defaultMessage: 'Last Name' }),
          labelDesc: '',
          required: true,
          maxLength: 31,
          type: 'string',
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'phone',
          label: `${formatMessage({ id: 'phone', defaultMessage: 'Mobile' })}`,
          labelDesc: '',
          type: 'string',
          required: true,
          className: 'md:col-span-2 col-span-4',
          maxLength: 20,
          validate(value) {
            return validatePhoneNumeric(value);
          },
        },
        {
          name: 'line1',
          label: `${formatMessage({ id: 'address', defaultMessage: 'Address' })} 1`,
          labelDesc: '',
          required: true,
          type: 'string',
          className: 'md:col-span-2 col-span-4',
          render() {
            if (enableAddress2) return <></>;

            return (
              <div className="col-span-3 mt-16 cursor-pointer">
                <p className="w-fit text-14 text-secondary-black" onClick={onEnableAddress2}>
                  + {formatCheckoutMessage({ id: 'add.address', defaultMessage: 'Add another address line' })}
                </p>
              </div>
            );
          },
        },
        ...(enableAddress2
          ? [
              {
                name: 'line2',
                label: `${formatMessage({ id: 'address', defaultMessage: 'Address' })} 2`,
                labelDesc: '',
                type: 'string',
                className: 'md:col-span-2 col-span-4',
              },
            ]
          : []),
        {
          name: 'city',
          label: formatMessage({ id: 'city', defaultMessage: 'Town/City' }),
          labelDesc: '',
          required: true,
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'county',
          label: formatMessage({ id: 'county', defaultMessage: 'County' }),
          labelDesc: '',
          required: false,
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'postalCode',
          label: formatMessage({ id: 'zipCode', defaultMessage: 'Postcode' }),
          labelDesc: '',
          required: true,
          className: 'md:col-span-2 col-span-4',
          validate(value, countryCode) {
            return validatePostalCode(value, countryCode || getCountry());
          },
        },
      ] as Fields[];
      if (!loggedIn) {
        fieldList.splice(2, 0, {
          name: 'email',
          label: formatMessage({ id: 'email', defaultMessage: 'Email' }),
          labelDesc: '',
          required: true,
          type: 'email',
          className: 'md:col-span-2 col-span-4',
          validate(value) {
            return validateEmail(value);
          },
        });
      }

      return fieldList;
    },
    [formatMessage, formatCheckoutMessage, validateEmail],
  );

  if (!selectedDeliveryOption) {
    return (
      <div className="mb-20">
        <Loading></Loading>
      </div>
    );
  }

  return (
    <div className="bg-white pt-16 lg:px-36 lg:pb-36 lg:pt-0">
      <div className="relative">
        <div className="relative bg-white" id="addressSection">
          {selectedDeliveryOption === 'StoreCollection' && (
            <>
              <CollectInStore
                isOpen={collectInStoreOverlayOpen}
                setIsOpen={setCollectInStoreOverlayOpen}
                active={active}
                setSameShippingAddress={setSameShippingAddress}
                setShippingAddress={setShippingAddress}
              />
              {shippingAddress.addressId !== '' && (
                <div className="border-neutral-400 lg:border lg:p-16">
                  <div className="mb-18 text-base">
                    {formatCheckoutMessage({
                      id: 'your.selected.store',
                      defaultMessage: 'Your selected store',
                    })}
                  </div>
                  <div className="flex justify-between">
                    <div className="border-l-4 border-green-500 py-18 pl-18">
                      <p className="text-14 text-secondary-black">
                        {`${shippingAddress.line1}${shippingAddress.line2 ? `, ${shippingAddress.line2}` : ''}`}
                        <span className="mt-8 block" />
                        {`${shippingAddress.postalCode} ${shippingAddress.city}`}
                        <span className="mt-8 block" />
                        {shippingAddress.email && `${shippingAddress.email}`}
                        <span className="mt-8 block" />
                        {shippingAddress.phone && `${shippingAddress.phone}`}
                      </p>
                    </div>
                    <div>
                      <div className="hidden md:block">
                        <Button
                          variant="underlined"
                          size="fit"
                          className="font-medium text-ag_color-text_green underline"
                          onClick={() => {
                            setCollectInStoreOverlayOpen(true);
                          }}
                        >
                          {formatCheckoutMessage({
                            id: 'select.another.store',
                            defaultMessage: 'Select another store',
                          })}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {shippingAddress.addressId === '' && (
                <div className="relative mt-auto block text-center">
                  <Button
                    variant="primary"
                    size="s"
                    className="w-full md:w-fit"
                    onClick={() => {
                      setCollectInStoreOverlayOpen(true);
                    }}
                  >
                    {formatCheckoutMessage({
                      id: 'select.your.local.store',
                      defaultMessage: 'Select your local store',
                    })}
                  </Button>
                </div>
              )}
              <div className="mt-48">
                {/* Adjusted logic to correctly handle the display of billing address form */}
                {loggedIn ? (
                  <>
                    <div className="my-18 flex items-center gap-8 lg:gap-12">
                      <h5 className="text-16 capitalize">
                        {formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'Billing Address' })}
                      </h5>
                      <Info
                        message={`${formatCheckoutMessage({
                          id: 'enter.associated.address.with.payment',
                          defaultMessage: 'Enter the address that is associated with your payment method',
                        })}.`}
                      />
                    </div>
                    {billingAddresses.length > 0 ? (
                      <AccountAddresses
                        type="billing"
                        className="mt-28"
                        onSelectAddress={setBillingAddress}
                        retainData={retainData}
                      />
                    ) : (
                      <AddressForm
                        className="mt-28 md:max-w-[100%]"
                        fields={fields}
                        address={billingAddress}
                        addressType="billing"
                        onChange={handleBillingAddressChange}
                        onAFDAddressSelection={onAFDAddressSelection}
                        onAddressCountrySelection={onAddressCountrySelection}
                        onSelectedTitle={onSelectedTitle}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <div className="my-18 flex items-center gap-8 lg:gap-12">
                      <h5 className="text-16 capitalize">
                        {formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'Billing Address' })}
                      </h5>
                      <Info
                        message={`${formatCheckoutMessage({
                          id: 'enter.associated.address.with.payment',
                          defaultMessage: 'Enter the address that is associated with your payment method',
                        })}.`}
                      />
                    </div>
                    <AddressForm
                      className="mt-28 md:max-w-[100%]"
                      fields={fields}
                      address={billingAddress}
                      addressType="billing"
                      onChange={handleBillingAddressChange}
                      onAFDAddressSelection={onAFDAddressSelection}
                      onAddressCountrySelection={onAddressCountrySelection}
                      onSelectedTitle={onSelectedTitle}
                    />
                  </>
                )}
              </div>
            </>
          )}
          {selectedDeliveryOption !== 'StoreCollection' && (
            <>
              {loggedIn ? (
                <>
                  <div>
                    <div className="mb-18 flex items-center gap-8 lg:gap-12">
                      <h5 className="text-16 capitalize">
                        {formatCheckoutMessage({ id: 'shippingAddress', defaultMessage: 'Shipping Address' })}
                      </h5>
                      <Info
                        message={`${formatCheckoutMessage({
                          id: 'enter.associated.address.with.payment',
                          defaultMessage: 'Enter the address that is associated with your payment method',
                        })}.`}
                      />
                    </div>
                    {shippingAddresses.length > 0 ? (
                      <>
                        <AccountAddresses
                          className="mt-20"
                          type="shipping"
                          onSelectAddress={(address) => setShippingAddress(address)}
                          retainData={retainData}
                        />
                      </>
                    ) : (
                      <AddressForm
                        className="md:max-w-[100%]"
                        fields={fields}
                        addressType="shipping"
                        address={shippingAddress}
                        onChange={handleShippingAddressChange}
                        onAFDAddressSelection={onAFDAddressSelection}
                        onAddressCountrySelection={onAddressCountrySelection}
                        onSelectedTitle={onSelectedTitle}
                      />
                    )}
                  </div>
                  <div>
                    <div className="hite mt-28 flex items-center gap-12 p-2">
                      <Checkbox
                        label={formatCheckoutMessage({
                          id: 'billingDetailsLabel',
                          defaultMessage: 'My billing address is the same as my delivery address',
                        })}
                        labelPosition="on-right"
                        checked={sameShippingAddress}
                        onChange={handleCheckboxChange}
                      />
                    </div>
                    {!sameShippingAddress && (
                      <>
                        <div className="my-18 flex items-center gap-8 lg:gap-12">
                          <h5 className="text-16 capitalize">
                            {formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'Billing Address' })}
                          </h5>
                          <Info
                            message={`${formatCheckoutMessage({
                              id: 'enter.associated.address.with.payment',
                              defaultMessage: 'Enter the address that is associated with your payment method',
                            })}.`}
                          />
                        </div>
                        {billingAddresses.length > 0 ? (
                          <AccountAddresses
                            type="billing"
                            className="mt-28"
                            onSelectAddress={(address) => setBillingAddress(address)}
                            retainData={retainData}
                          />
                        ) : (
                          <>
                            <AddressForm
                              className="mt-28 md:max-w-[100%]"
                              fields={fields}
                              address={billingAddress}
                              addressType="billing"
                              onChange={handleBillingAddressChange}
                              onAFDAddressSelection={onAFDAddressSelection}
                              onAddressCountrySelection={onAddressCountrySelection}
                              onSelectedTitle={onSelectedTitle}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-18 flex items-center gap-8 lg:gap-12">
                    <h5 className="text-16 capitalize">
                      {formatCheckoutMessage({ id: 'shippingAddress', defaultMessage: 'Shipping Address' })}
                    </h5>
                  </div>
                  <AddressForm
                    className="md:max-w-[100%]"
                    fields={fields}
                    addressType="shipping"
                    address={shippingAddress}
                    onChange={handleShippingAddressChange}
                    onAFDAddressSelection={onAFDAddressSelection}
                    onAddressCountrySelection={onAddressCountrySelection}
                    onSelectedTitle={onSelectedTitle}
                  />
                  <div className="hite mt-28 flex items-center gap-12 p-2">
                    <Checkbox
                      label={formatCheckoutMessage({
                        id: 'billingDetailsLabel',
                        defaultMessage: 'My billing address is the same as my delivery address',
                      })}
                      labelPosition="on-right"
                      checked={sameShippingAddress}
                      onChange={handleCheckboxChange}
                      disableBackground
                    />
                  </div>
                  {!sameShippingAddress && (
                    <>
                      <div className="my-18 flex items-center gap-8 lg:gap-12">
                        <h5 className="text-16 capitalize">
                          {formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'billing Address' })}
                        </h5>
                        <Info
                          message={`${formatCheckoutMessage({
                            id: 'enter.associated.address.with.payment',
                            defaultMessage: 'Enter the address that is associated with your payment method',
                          })}.`}
                        />
                      </div>
                      <AddressForm
                        className="mt-28 md:max-w-[100%]"
                        fields={fields}
                        address={billingAddress}
                        addressType="billing"
                        onChange={handleBillingAddressChange}
                        onAFDAddressSelection={onAFDAddressSelection}
                        onAddressCountrySelection={onAddressCountrySelection}
                        onSelectedTitle={onSelectedTitle}
                        restrictEmailField={true}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
          {selectedDeliveryOption === 'HomeDelivery' && (
            <div className="mt-40 md:mt-56">
              <h2 className="mb-18 text-16">Delivery Instructions</h2>
              <p className="text-14 font-light">
                Please supply a door code if required to access your address and/or preferred alternative delivery
                location if no answer.
              </p>
              <Input variant="primary" onChange={handleDeliveryInstruction} value={deliveryInstruction ?? ''}></Input>
              <p className="mt-6 text-right text-14">
                {deliveryInstruction ? 32 - deliveryInstruction?.length : 32} characters remaining
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-28 md:mt-36 lg:mt-45">
        {
          <Button
            variant="primary"
            className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36"
            disabled={isButtonDisabled}
            loading={processing}
            type="submit"
            onClick={handleButtonClick}
            size="m"
          >
            {formatCartMessage({ id: 'continue.to', defaultMessage: 'Continue to' })}{' '}
            <span className="text-14 font-bold uppercase">
              {selectedDeliveryOption === 'StoreCollection'
                ? formatCartMessage({ id: 'payment', defaultMessage: 'Payment' })
                : formatCartMessage({ id: 'shipping', defaultMessage: 'Shipping' })}
            </span>
          </Button>
        }
      </div>
    </div>
  );
};

export default Addresses;
